import {
  Bell,
  CheckCheck,
  Filter as FilterIcon,
  FileStack,
  MessageCircleQuestion,
} from 'lucide-react';
import { type ComponentType, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/DropdownMenu';
import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/Popover';
import { cn } from '@shared/components/ui/cn';
import {
  type AccountNotification,
  type NotificationFilter,
  useAccountNotifications,
} from '@shared/hooks/use-account-notifications';
import {
  categorizeNotification,
  type NotificationCategory,
} from '@shared/lib/notifications/categorize';
import { formatNotificationTimestamp } from '@shared/lib/notifications/format-time';
import { getNotificationPath } from '@shared/lib/notifications/routing';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CATEGORY_ICON: Record<NotificationCategory, ComponentType<any>> = {
  'client-request': MessageCircleQuestion,
  vault: FileStack,
};

const CATEGORY_COLOR: Record<NotificationCategory, string> = {
  'client-request': 'text-blue-500',
  vault: 'text-amber-500',
};

interface NotificationCenterProps {
  onNavigate: (path: string) => void;
}

export function NotificationCenter({ onNavigate }: NotificationCenterProps) {
  const { t } = useTranslation('notifications');
  const { items, unreadCount, fetching, markAsRead, markAllAsRead } = useAccountNotifications();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const [open, setOpen] = useState(false);

  const filtered = items.filter((n) => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !n.viewed;
    return categorizeNotification(n) === filter;
  });

  const handleOpenNotification = (n: AccountNotification) => {
    if (!n.viewed) markAsRead([n.id]);
    const path = getNotificationPath(n, categorizeNotification(n));
    setOpen(false);
    if (path) onNavigate(path);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="relative flex size-9 items-center justify-center rounded-full border bg-background shadow-sm text-foreground/70 transition-colors hover:bg-accent hover:text-foreground"
          aria-label={unreadCount > 0 ? t('aria.unread', { count: unreadCount }) : t('title')}
        >
          <Bell className="size-4" />
          {unreadCount > 0 && (
            <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={8} className="w-[380px] p-0">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-semibold">{t('title')}</h3>
          <div className="flex items-center gap-1">
            {/* Filter menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
                  aria-label={t('filters.label')}
                >
                  <FilterIcon className="size-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(['all', 'unread', 'client-request', 'vault'] as const).map((value) => (
                  <DropdownMenuItem
                    key={value}
                    onSelect={() => {
                      setFilter(value);
                    }}
                  >
                    {t(`filters.${value === 'client-request' ? 'clientRequest' : value}`)}
                    {filter === value && ' ✓'}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mark all read */}
            <button
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-40"
              title={t('markAllRead')}
              aria-label={t('markAllRead')}
            >
              <CheckCheck className="size-3.5" />
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div className="max-h-[400px] overflow-y-auto">
          {filtered.length === 0 ? (
            <EmptyState loading={fetching} />
          ) : (
            filtered.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                onOpen={() => {
                  handleOpenNotification(n);
                }}
              />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NotificationItem({
  notification: n,
  onOpen,
}: {
  notification: AccountNotification;
  onOpen: () => void;
}) {
  const { t } = useTranslation('notifications');
  const category = categorizeNotification(n);
  const Icon = category ? CATEGORY_ICON[category] : Bell;
  const color = category ? CATEGORY_COLOR[category] : 'text-muted-foreground';
  const timestamp = n.createdAt ?? n.receivedDate;

  const titleId = useId();
  const descriptionId = useId();
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  // Whether the collapsed (2-line) body hides text. Measured, not guessed from the length, so
  // the toggle shows up only when something is actually cut off. Not re-measured while
  // expanded — the full text fits by definition — so "Show less" stays available.
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    const el = descriptionRef.current;
    if (!el || expanded) return;
    const measure = () => {
      setClipped(el.scrollHeight > el.clientHeight);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [expanded, n.description]);

  return (
    // The row is a <button> (deep link + mark as read), so the expand toggle has to be its
    // sibling: nested inside, a click on it would also open the notification.
    <div
      className={cn(
        'flex flex-col items-start transition-colors hover:bg-accent',
        !n.viewed && 'bg-accent/30',
      )}
    >
      <button onClick={onOpen} className="flex w-full gap-3 px-4 py-3 text-start">
        <div className="mt-0.5 shrink-0">
          <Icon className={cn('size-4', color)} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p id={titleId} className={cn('text-sm', !n.viewed && 'font-medium')}>
              {n.title}
            </p>
            {!n.viewed && <span className="mt-1 size-2 shrink-0 rounded-full bg-primary" />}
          </div>
          {n.description && (
            <p
              id={descriptionId}
              ref={descriptionRef}
              className={cn(
                'mt-0.5 whitespace-pre-line break-words text-xs text-muted-foreground',
                !expanded && 'line-clamp-2',
              )}
            >
              {n.description}
            </p>
          )}
          {timestamp && (
            <p className="mt-1 text-[11px] text-muted-foreground/70">
              {formatNotificationTimestamp(timestamp)}
            </p>
          )}
        </div>
      </button>
      {n.description && (expanded || clipped) && (
        // Aligned with the text column: row padding (px-4) + icon (size-4) + gap (gap-3). The
        // padding sits on the wrapper so the focus ring hugs the label, not the empty gutter.
        <div className="mb-2.5 ps-11 pe-4">
          <button
            type="button"
            onClick={() => {
              setExpanded((value) => !value);
            }}
            aria-expanded={expanded}
            aria-controls={descriptionId}
            // The label alone is the same on every row; the title tells screen readers which one.
            aria-describedby={titleId}
            className="rounded-sm text-xs font-medium text-primary hover:underline"
          >
            {expanded ? t('showLess') : t('showMore')}
          </button>
        </div>
      )}
    </div>
  );
}

function EmptyState({ loading }: { loading: boolean }) {
  const { t } = useTranslation('notifications');
  return (
    <div className="flex flex-col items-center justify-center gap-4 px-6 py-14 text-center">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-muted">
        <Bell className="size-6 text-foreground" />
      </div>
      <div className="space-y-1">
        <p className="text-base font-semibold text-foreground">
          {loading ? t('loading') : t('empty.title')}
        </p>
        {!loading && <p className="text-sm text-muted-foreground">{t('empty.description')}</p>}
      </div>
    </div>
  );
}
